import { defineComponent as _defineComponent } from 'vue'
import { library } from '@fortawesome/fontawesome-svg-core';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import Vue, { computed, ref } from 'vue';
import { useRouter } from 'vue-router/composables';

import i18n from '@/initI18n';

import convertImportActivityToActivity from '@/activities/importActivities/utils/convertImportActivitiesToActivities';
import TheContentbar from '@/layout/components/TheContentbar.vue';
import ThePageSkeleton from '@/layout/components/ThePageSkeleton.vue';
import { Api } from '@/plugins/farmdokRestApi';
import Steps from '@/shared/components/Steps.vue';
import Button from '@/shared/components/buttons/Button.vue';
import ButtonDropdown from '@/shared/components/buttons/ButtonDropdown.vue';
import useToast from '@/shared/composables/useToast';
import store from '@/store';

import useActivitiesImports from './importActivities/composables/useActivitiesImport';
import useCnhActivityImport from './importActivities/composables/useCnhActivityImport';
import { Getters } from './importActivities/store/getters';


export default /*#__PURE__*/_defineComponent({
  __name: 'PageActivitiesImport',
  setup(__props) {

library.add(faArrowsRotate);

const { activityImportApi } = Vue.prototype.$api as Api;

const $t = i18n.translate;
const { state, currentStep, goBack, goNext } = useActivitiesImports();
const { pollingInProgress, resetImport } = useCnhActivityImport();
const router = useRouter();
const { toast } = useToast();

const steps = [
  { number: 1, label: $t('Datenquelle') },
  { number: 2, label: $t('Daten laden') },
  { number: 3, label: $t('Vorschau') },
];

const buttonUpdateSelection = [
  {
    text: $t('Auswahl aufheben'),
    handler: () => view.value.tableElement.selectAllVisible(false),
  },
  {
    text: $t('Auswahl umkehren'),
    handler: () => view.value.tableElement.invertSelection(),
  },
  {
    text: $t('Alle auswählen'),
    handler: () => view.value.tableElement.selectAllVisible(true),
  },
];

/* -- REACTIVE -- */

const view = ref();

/* -- COMPUTED -- */

const isImportButtonEnabled = computed(() => {
  const selectedImportActivityIds = store.getters['activities/importActivities/selectedImportActivityIds'];

  return (
    currentStep.value !== 2 ||
    (selectedImportActivityIds.length > 0 && selectedImportActivitiesValid(selectedImportActivityIds))
  );
});

/* -- METHODS -- */

async function resetMapping() {
  const response = await Vue.prototype.$confirm({
    okTitle: $t('Zurücksetzen') ?? 'Zurücksetzen',
    okVariant: 'danger',
  });
  if (!response) {
    return;
  }

  const companyId = store.state.auth.currentCompanies?.[0]?.id;
  await activityImportApi.activityImportResetMappings({
    activityImportResetMappingsRequest: { companyId },
  });
  store.dispatch('activities/importActivities/subscribe', { forceRefresh: true });
}

function removeImportActivitiesAndReset() {
  Object.values(store.state.activities.importActivities.data).forEach((importActivity) => {
    store.commit('activities/importActivities/removeImportActivity', importActivity.id);
  });
  resetImport();
}

function selectedImportActivitiesValid(selectedImportActivityIds: string[]) {
  return selectedImportActivityIds.every(
    (selectedImportActivityId) =>
      store.getters['activities/importActivities/isImportActivityValid'](
        selectedImportActivityId,
      ) as Getters['isImportActivityValid'],
  );
}

async function startImport() {
  const { importActivities } = store.state.activities;
  const tableDataImportActivities = store.getters[
    'activities/importActivities/tableDataImportActivities'
  ] as Getters['tableDataImportActivities'];
  const selectedImportActivities = Object.values(importActivities.data).filter(
    (importActivity) =>
      tableDataImportActivities.find((tableDataActivity) => tableDataActivity.id === importActivity.id)?.select,
  );

  if (!selectedImportActivitiesValid(selectedImportActivities.map((activity) => activity.id))) {
    throw new Error(`There are invalid import activities selected.`);
  }

  const activities = convertImportActivityToActivity(selectedImportActivities);
  if (activities.length > 0) {
    await activities.forEach((activity) => store.dispatch('activities/insertNewEntry', { entry: activity }));
    await store.dispatch('activities/syncAll');

    // check if there are non successful imports
    const { errors } = store.state.activities;

    removeImportActivitiesAndReset();

    await router.push({ name: 'activities' });

    activities.forEach((activity) => {
      const foundError = errors.find((error) => error.guid === activity.id);

      if (foundError) {
        toast($t('Aktivität konnte nicht importiert werden. Fehler: ') + foundError.errorUserMessage, 'warning');
      } else {
        store.commit('activities/addOrUpdateTableDataMetadataByKeyAndValue', {
          activityId: activity.id,
          key: 'select',
          value: true,
        });
      }
    });
  }
}

return { __sfc: true,activityImportApi, $t, state, currentStep, goBack, goNext, pollingInProgress, resetImport, router, toast, steps, buttonUpdateSelection, view, isImportButtonEnabled, resetMapping, removeImportActivitiesAndReset, selectedImportActivitiesValid, startImport, TheContentbar, ThePageSkeleton, Steps, Button, ButtonDropdown }
}

})